import React from 'react';
import { Navigate, Outlet, useParams } from 'react-router-dom';
import { useUser } from '@clerk/clerk-react';
import { useBranding } from '../contexts/BrandingContext';

/**
 * Guard para rutas de tenant administrador.
 * Valida que:
 * 1. El usuario tiene role 'admin' (no superadmin, no client)
 * 2. El tenantCode de la URL coincide con el tenantCode en su metadata de Clerk
 *
 * Si no coincide o no tiene tenantCode, redirige a su /:tenantCode/dashboard correcto.
 * Si el rol no es admin, redirige a /login.
 */
const TenantRoute: React.FC = () => {
    const { user, isLoaded } = useUser();
    const { tenantCode } = useParams<{ tenantCode: string }>();
    const { branding, isLoading: isBrandingLoading } = useBranding();

    if (!isLoaded) {
        return <div className="flex justify-center items-center h-screen">Cargando...</div>;
    }

    if (isBrandingLoading) {
        return <div className="flex justify-center items-center h-screen">Cargando contexto de sucursal...</div>;
    }

    const role = user?.publicMetadata?.role as string | undefined;
    const userTenantCode = user?.publicMetadata?.tenantCode as string | undefined;
    const branchAccessMode = user?.publicMetadata?.branchAccessMode as string | undefined;
    const branchCode = user?.publicMetadata?.branchCode as string | undefined;
    const branchCodes = Array.isArray(user?.publicMetadata?.branchCodes) ? (user?.publicMetadata?.branchCodes as string[]) : [];
    const rootTenantCode = user?.publicMetadata?.rootTenantCode as string | undefined;

    // Super admin no debe entrar a rutas de tenant
    if (role === 'superadmin') {
        return <Navigate to="/admin/tenants" replace />;
    }

    // Sin tenantCode en metadata → no es admin de tenant
    if (!userTenantCode) {
        return <Navigate to="/login" replace />;
    }

    if (branchAccessMode === 'single') {
        const allowedTenantCode = branchCode || userTenantCode;
        if (tenantCode !== allowedTenantCode) {
            return <Navigate to={`/${allowedTenantCode}/dashboard`} replace />;
        }

        return <Outlet />;
    }

    if (branchAccessMode === 'subset' && branchCodes.length > 0) {
        if (!tenantCode || !branchCodes.includes(tenantCode)) {
            return <Navigate to={`/${branchCodes[0]}/dashboard`} replace />;
        }

        return <Outlet />;
    }

    if (branchAccessMode === 'all' && rootTenantCode && branding?.rootTenantCode && branding.rootTenantCode !== rootTenantCode) {
        const fallbackTenantCode = branchCode || userTenantCode;
        return <Navigate to={`/${fallbackTenantCode}/dashboard`} replace />;
    }

    // Si la URL tiene un tenantCode diferente al del usuario, corregirlo
    if (tenantCode !== userTenantCode && branchAccessMode !== 'all') {
        return <Navigate to={`/${userTenantCode}/dashboard`} replace />;
    }

    return <Outlet />;
};

export default TenantRoute;
